// Food: what a burger is made of, how a patty and a portion of fries cook,
// and whether a finished basket is what a ticket asked for. Pure data and
// functions; `shift.ts` moves food between the stations.

export type Topping = "cheese" | "bacon" | "lettuce" | "tomato" | "onion" | "pickles";

/** The topping bins' order along the assembly board, left to right. */
export const TOPPINGS: readonly Topping[] = ["cheese", "bacon", "lettuce", "tomato", "onion", "pickles"];

export const TOPPING_NAMES: Record<Topping, string> = {
  cheese: "cheese",
  bacon: "bacon",
  lettuce: "lettuce",
  tomato: "tomato",
  onion: "onion",
  pickles: "pickles",
};

/** The soda machine's four nozzles, in order from your left as you face it. */
export type Drink = "water" | "pop" | "diet" | "lemon";
export const DRINKS: readonly Drink[] = ["water", "pop", "diet", "lemon"];
export const DRINK_NAMES: Record<Drink, string> = { water: "water", pop: "pop", diet: "diet pop", lemon: "lemon pop" };

/**
 * What a ticket asks for: a burger (if `patties` > 0) and maybe fries in a
 * basket, and maybe a drink in a cup.
 */
export interface Build {
  patties: number;
  toppings: Topping[];
  fries: boolean;
  drink?: Drink;
}

/** Whether a build needs a basket (anything but a drink on its own). */
export function needsBasket(b: Build): boolean {
  return b.patties > 0 || b.fries || b.toppings.length > 0;
}

/** A cup is right once it's filled to `FULL_AT` of the way, and over the brim past 1 it spills. */
export const FULL_AT = 0.85;
export const SPILL_AT = 1;
/** How far over the brim a cup can go: it's spilling everywhere by then. */
export const MAX_FILL = 1.15;

/** A cup from the soda machine: what's in it and how full, 0 empty to 1 at the brim. */
export interface Glass {
  drink: Drink | null;
  fill: number;
}

export type GlassStatus = "empty" | "short" | "full" | "spilled";

export function glassStatus(g: Glass): GlassStatus {
  if (!g.drink || g.fill <= 0) return "empty";
  if (g.fill > SPILL_AT) return "spilled";
  return g.fill >= FULL_AT ? "full" : "short";
}

/** What's wrong with a cup for a drink, in words, or null when it's right. */
export function drinkMismatch(g: Glass, drink: Drink): string | null {
  const status = glassStatus(g);
  if (status === "empty") return `needs a ${DRINK_NAMES[drink]}`;
  if (g.drink !== drink) return `wanted ${DRINK_NAMES[drink]}, not ${DRINK_NAMES[g.drink!]}`;
  if (status === "short") return `the ${DRINK_NAMES[drink]}'s not full`;
  if (status === "spilled") return `the ${DRINK_NAMES[drink]} spilled over: tip it out`;
  return null;
}

/**
 * A patty on the flat-top: how cooked each side is, the side down first.
 * Heat only reaches the side that's down, so it has to be flipped. A side is
 * done at 1 and burnt at `BURNT`.
 */
export interface Patty {
  sides: [number, number];
}

/** A portion of fries: done at 1, burnt at `FRIES_BURNT`. */
export interface Fries {
  cook: number;
}

export const BURNT = 2;
export const FRIES_BURNT = 2;

export type Doneness = "raw" | "cooking" | "done" | "burnt";

export function pattyDoneness(p: Patty): Doneness {
  if (p.sides[0] >= BURNT || p.sides[1] >= BURNT) return "burnt";
  if (p.sides[0] >= 1 && p.sides[1] >= 1) return "done";
  return p.sides[0] > 0 || p.sides[1] > 0 ? "cooking" : "raw";
}

export function friesDoneness(f: Fries): Doneness {
  if (f.cook >= FRIES_BURNT) return "burnt";
  if (f.cook >= 1) return "done";
  return f.cook > 0 ? "cooking" : "raw";
}

/** A patty's down side has cooked through and its up side hasn't: time to flip. */
export function needsFlip(p: Patty): boolean {
  return p.sides[0] >= 1 && p.sides[1] < 1;
}

/** What's in a basket on the board or in your hands. A burger's buns come with its first patty. */
export interface Contents {
  patties: Patty[];
  toppings: Topping[];
  fries: Fries | null;
}

export const EMPTY: Contents = { patties: [], toppings: [], fries: null };

/** The first way a basket falls short of a build, in words, or null when it's right. */
export function mismatch(c: Contents, b: Build): string | null {
  if (c.patties.length < b.patties) return b.patties - c.patties.length === 1 && c.patties.length ? "needs another patty" : "needs a patty";
  if (c.patties.length > b.patties) return b.patties ? "has a patty too many" : "shouldn't have a burger";
  const raw = c.patties.find((p) => pattyDoneness(p) !== "done");
  if (raw) return pattyDoneness(raw) === "burnt" ? "has a burnt patty" : "has an undercooked patty";
  for (const t of b.toppings) if (!c.toppings.includes(t)) return `needs ${TOPPING_NAMES[t]}`;
  for (const t of c.toppings) if (!b.toppings.includes(t)) return `no ${TOPPING_NAMES[t]} on this one`;
  if (b.fries && !c.fries) return "needs fries";
  if (!b.fries && c.fries) return "no fries with this one";
  if (c.fries && friesDoneness(c.fries) !== "done") return friesDoneness(c.fries) === "burnt" ? "has burnt fries" : "has soggy, undercooked fries";
  return null;
}

/** The build, in words: "2 patties, cheese, onion + fries + pop". */
export function describeBuild(b: Build): string {
  const parts: string[] = [];
  if (b.patties) parts.push(b.patties === 1 ? "patty" : `${b.patties} patties`, ...b.toppings.map((t) => TOPPING_NAMES[t]));
  const items = [parts.join(", "), b.fries ? "fries" : "", b.drink ? DRINK_NAMES[b.drink] : ""].filter(Boolean);
  return items.join(" + ");
}

/** A build as short as it'll go, for a ticket slip: "double, cheese, onion + fries". */
export function shortBuild(b: Build): string {
  const parts = [...(b.patties > 1 ? ["double"] : []), ...b.toppings.map((t) => TOPPING_NAMES[t])];
  if (!b.fries) return parts.join(", ") || "plain";
  return parts.length ? `${parts.join(", ")} + fries` : b.patties ? "plain + fries" : "fries";
}

/**
 * How far past done food gets before it's "burning": a warning, while
 * there's still time to save it. A fraction of the way from done to burnt.
 */
export const BURNING_AT = 0.6;

/** What food on the heat needs from you, at a glance. */
export type CookStatus = "cooking" | "flip" | "done" | "burning" | "burnt";

/** A patty on the flat-top: cooking (with how far its down side's got), time to flip, done, about to burn, or burnt. */
export function pattyStatus(p: Patty): { status: CookStatus; progress: number } {
  const warn = 1 + BURNING_AT * (BURNT - 1);
  if (pattyDoneness(p) === "burnt") return { status: "burnt", progress: 1 };
  if (p.sides[0] >= 1) return { status: p.sides[0] >= warn ? "burning" : p.sides[1] >= 1 ? "done" : "flip", progress: 1 };
  return { status: "cooking", progress: Math.min(1, p.sides[0]) };
}

/** Fries in the fryer: cooking (with how far), done, about to burn, or burnt. */
export function friesStatus(f: Fries): { status: CookStatus; progress: number } {
  const warn = 1 + BURNING_AT * (FRIES_BURNT - 1);
  if (f.cook >= FRIES_BURNT) return { status: "burnt", progress: 1 };
  if (f.cook >= 1) return { status: f.cook >= warn ? "burning" : "done", progress: 1 };
  return { status: "cooking", progress: Math.max(0, f.cook) };
}
